import React from 'react';
import { SchematicShell, Rect } from './SchematicPrimitives';
import {
  TABLE,
  clampSpec,
} from '../../3d/geometry/proceduralRenderers/proceduralSpecs';

/**
 * Elevation schematic for `table` — the Track-Arm table.
 *
 * Matches `tableRenderer` in furnitureRenderers.js (shared proportions live in
 * `proceduralRenderers/proceduralSpecs.js` → TABLE): a single thin top on four
 * thin matte-black metal pin legs, tied low by a slim stretcher rail. No thick
 * apron.
 *
 * Front + side share one adaptive profile (the top spans the full viewport in
 * both, legs inset by `legInset`, low stretcher at `stretcherHeightFrac` of the
 * leg height). Right view mirrors left.
 */

export function TableSchematic({
  widthMeters: width,
  heightMeters: height,
  depthMeters: depth,
  view = 'front',
  strokeColor,
  strokeWidth,
}) {
  const isSide = view === 'left' || view === 'right';
  const viewportW = isSide ? depth : width;
  const inner = (
    <Profile
      viewportW={viewportW}
      width={width}
      depth={depth}
      height={height}
      strokeColor={strokeColor}
      strokeWidth={strokeWidth}
    />
  );
  return (
    <SchematicShell
      width={viewportW}
      height={height}
      strokeColor={strokeColor}
      strokeWidth={strokeWidth}
      drawFrame={false}
    >
      {view === 'right' ? (
        <g transform={`scale(-1, 1) translate(${-viewportW}, 0)`}>{inner}</g>
      ) : (
        inner
      )}
    </SchematicShell>
  );
}

function Profile({
  viewportW,
  width,
  depth,
  height,
  strokeColor,
  strokeWidth,
}) {
  const minFoot = Math.min(width, depth);
  const topThick = clampSpec(
    height * TABLE.topThickFrac,
    TABLE.topThickMin,
    TABLE.topThickMax
  );
  const legR = clampSpec(
    minFoot * TABLE.legRadiusFrac,
    TABLE.legRadiusMin,
    TABLE.legRadiusMax
  );
  const legW = 2 * legR;
  const inset = clampSpec(
    minFoot * TABLE.legInsetFrac,
    TABLE.legInsetMin,
    TABLE.legInsetMax
  );
  const legCx = [inset, viewportW - inset];
  const legTopY = topThick;
  const legBottomY = height;
  // Leg height as the renderer computes it (top underside + penetration to floor).
  const legH = height - topThick + TABLE.legPenetration;
  const stretcherY = height - legH * TABLE.stretcherHeightFrac;
  const railThick = clampSpec(
    legR * TABLE.stretcherThickFrac,
    TABLE.stretcherThickMin,
    TABLE.stretcherThickMax
  );

  return (
    <g>
      {/* Thin top slab spanning the full viewport. */}
      <Rect
        x={0}
        y={0}
        width={viewportW}
        height={topThick}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      {/* Four legs (two visible in elevation). */}
      {legCx.map((cx, i) => (
        <Rect
          key={`leg-${i}`}
          x={cx - legR}
          y={legTopY}
          width={legW}
          height={legBottomY - legTopY}
          strokeColor={strokeColor}
          strokeWidth={strokeWidth}
        />
      ))}
      {/* Low slim stretcher rail tying the legs. */}
      {stretcherY > legTopY && (
        <Rect
          x={legCx[0]}
          y={stretcherY - railThick / 2}
          width={legCx[1] - legCx[0]}
          height={railThick}
          strokeColor={strokeColor}
          strokeWidth={strokeWidth}
        />
      )}
    </g>
  );
}

TableSchematic.supportsSideViews = true;
